import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, UserPlus, Save } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PROFILES = [
  { key: 'particulier', label: 'Particulier' },
  { key: 'entreprise', label: 'Entreprise' },
  { key: 'association', label: 'Association' },
  { key: 'ce', label: 'Comité d\'entreprise (CE)' },
  { key: 'groupe', label: 'Groupe' },
];

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13.5,
  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
  color: 'var(--admin-text)', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 5, display: 'block' };

/**
 * Modal de création d'une fiche client (CRM, sans mot de passe).
 * Props : onClose(), onCreated(client)
 */
export default function ClientCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', telephone: '', profile: 'particulier', adresse: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');

  function set(key, val) { setForm(prev => ({ ...prev, [key]: val })); }

  async function submit() {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Client créé : ${data.prenom || ''} ${data.nom}`.trim());
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la création');
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto"
      style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="client-create-modal">
      <div className="min-h-screen flex items-start justify-center p-4 py-10">
        <div className="w-full max-w-lg rounded-2xl"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-5"
            style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <div className="flex items-center gap-2">
              <UserPlus size={18} style={{ color: 'var(--admin-teal)' }} />
              <h2 className="font-bold text-lg" style={{ color: 'var(--admin-text)' }}>Nouveau client</h2>
            </div>
            <button onClick={onClose} style={{ color: 'var(--admin-muted)' }} data-testid="client-create-close">
              <X size={20} />
            </button>
          </div>

          {/* Formulaire */}
          <div className="p-5" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Prénom</label>
                <input style={inputStyle} value={form.prenom} data-testid="client-prenom-input"
                  onChange={e => set('prenom', e.target.value)} placeholder="Sophie" />
              </div>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input style={inputStyle} value={form.nom} data-testid="client-nom-input"
                  onChange={e => set('nom', e.target.value)} placeholder="Lefèvre" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input style={inputStyle} type="email" value={form.email} data-testid="client-email-input"
                  onChange={e => set('email', e.target.value)} placeholder="sophie@exemple.fr" />
              </div>
              <div>
                <label style={labelStyle}>Téléphone</label>
                <input style={inputStyle} value={form.telephone} data-testid="client-telephone-input"
                  onChange={e => set('telephone', e.target.value)} placeholder="06 xx xx xx xx" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Profil tarifaire</label>
              <select style={inputStyle} value={form.profile} data-testid="client-profile-select"
                onChange={e => set('profile', e.target.value)}>
                {PROFILES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Adresse</label>
              <input style={inputStyle} value={form.adresse} data-testid="client-adresse-input"
                onChange={e => set('adresse', e.target.value)} placeholder="12 rue des Lilas, Figeac" />
            </div>
            <div>
              <label style={labelStyle}>Notes internes</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={form.notes}
                data-testid="client-notes-input"
                onChange={e => set('notes', e.target.value)} placeholder="Informations utiles..." />
            </div>

            <button onClick={submit} disabled={saving || !form.nom.trim()}
              data-testid="client-create-submit"
              className="w-full flex items-center justify-center gap-2 mt-1"
              style={{
                padding: '11px 16px', borderRadius: 11, fontSize: 14, fontWeight: 700,
                background: form.nom.trim() ? 'var(--admin-teal)' : 'var(--admin-surface2)',
                color: form.nom.trim() ? '#052b25' : 'var(--admin-faint)', border: 'none',
                cursor: form.nom.trim() ? 'pointer' : 'not-allowed', opacity: saving ? 0.6 : 1,
              }}>
              <Save size={15} /> {saving ? 'Création...' : 'Créer le client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
